import {
  Button,
  Card,
  Collection,
  Flex,
  Label,
  SwitchField,
} from "@aws-amplify/ui-react"
import { isWaterGroup } from "@strtmn/sds/recipe-management"
import {
  FeedComponentCreateSchema,
  FeedComponentDefaultGroup,
  FeedComponentSchemaNameMaxLength,
  FeedComponentUpdateSchema,
} from "@strtmn/sds/schemas"
import type {
  FeedComponentCreateDto,
  FeedComponentDto,
  FeedComponentUpdateDto,
  OrganizationDto,
  ResponseMessage,
} from "@strtmn/sds/types"
import { post, put } from "aws-amplify/api"
import { Cache, I18n } from "aws-amplify/utils"
import { useContext, useState } from "react"
import {
  Controller,
  FormProvider,
  useForm,
  type SubmitHandler,
} from "react-hook-form"
// import { FaRegTrashCan } from "react-icons/fa6"
import { useNavigate, useParams } from "react-router-dom"
import { delToApi } from "../../../api/utils"
import { CancelButton, Modal } from "../../../components"
import { isProd } from "../../../config"
import { UserDataContext, UserDataContextHelper } from "../../../contexts"
import { useIsReadOnly } from "../../../hooks"
import { getLinkToOrgFeedComponents } from "../../../LinkUtils"
import {
  CURRENCY_SYMBOL,
  pricePerKgToPerLargeMass,
  pricePerLargeMassToPerKg,
  UNIT_OF_LARGE_MASS,
} from "../../../org-settings"
import { currentSession } from "../../../Utils.AmplifyAuth"
import {
  ensureImagePath,
  FEED_COMPONENT_GROUP_OPTIONS,
  getImagePath,
} from "../-components/FeedComponentUtils"
import {
  transformInputToNumber,
  transformNumberToString,
} from "../recipes/RecipeUtils"

import "../RecipeManagement.css"

type ComponentModes = "create" | "edit" | "invalid"

/** Cleans the form data before submission. */
export function cleanData(
  data: FeedComponentCreateDto,
  isCreateMode: boolean
): FeedComponentCreateDto | FeedComponentUpdateDto {
  console.debug("▶️ cleanData")
  /** @ts-ignore - user input can be string, or the field left untouched */
  if ("" === data.ts_gehalt_percent || undefined === data.ts_gehalt_percent) {
    data.ts_gehalt_percent = 0
  }
  /** @ts-ignore - user input can be string */
  if ("" === data.price_per_kg) {
    delete data.price_per_kg
  } else if (typeof data.price_per_kg === "number") {
    // The form shows the price per UNIT_OF_LARGE_MASS, but it's stored per kg.
    data.price_per_kg = pricePerLargeMassToPerKg(data.price_per_kg)
  }
  let cleaned
  if (isCreateMode) {
    cleaned = FeedComponentCreateSchema.cast(data)
    delete cleaned.id
  } else {
    cleaned = FeedComponentUpdateSchema.cast(data)
  }
  // Water has 0% dry matter by definition, regardless of what's displayed.
  if (isWaterGroup(cleaned.group)) {
    cleaned.ts_gehalt_percent = 0
  }
  cleaned.is_dry_mass = Number(cleaned.ts_gehalt_percent) > 0
  return cleaned
}

const getEmptyFeedComponent = (org: OrganizationDto): FeedComponentDto => {
  return {
    group: FeedComponentDefaultGroup,
    id: "",
    image: undefined,
    include_in_stats: true,
    name: "",
    org_id: org.id,
    is_dry_mass: false,
    price_per_kg: undefined,
    ts_gehalt_percent: 0,
    elevatorband_geschwindigkeit: 0,
    created_at: "",
    created_by: "",
    updated_at: "",
    updated_by: "",
  }
}

const componentImages = [
  {
    title: "Maissilage",
    href: "shutterstock_1429081703_1301.jpg",
  },
  {
    title: "Grassilage",
    href: "bsl-grassilage-rotiert_1006.jpg",
  },
  {
    title: "Ballensilage",
    href: "hay-bales-167539_1920.jpg",
  },
  {
    title: "Stroh",
    href: "straw-1649857_1920.jpg",
  },
  {
    title: "Heu",
    href: "hay-337915_1920.jpg",
  },
  {
    title: "Wasser",
    href: "drop-545377_1165-2.jpg",
  },
  {
    title: "Rapsschrot",
    href: "bsl-schrot_560.jpg",
  },
  {
    title: "Sojaschrot",
    href: "soy-bean-4994576_560.jpg",
  },
  {
    title: "Kraftfutter",
    href: "feed-2615928_560.jpg",
  },
  {
    title: "CCM",
    href: "unknown.png",
  },
  {
    title: "Biertreber",
    href: "bsl-biertreber_560.jpg",
  },
  {
    title: "Rübenschnitzel",
    href: "bsl-zucker-pellets_506.jpg",
  },
  {
    title: "Melasse",
    href: "unknown.png",
  },
  {
    title: "Mineralfutter",
    href: "bsl-granulat_560.jpg",
  },
  {
    title: "Sonstiges",
    href: "unknown.png",
  },
]

export default function FeedComponentCreateEdit() {
  const params = useParams()
  const navigate = useNavigate()
  const { userData, reloadUserDataAsync } = useContext(UserDataContext)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)

  const isReadOnly = useIsReadOnly()
  const isCreateMode = window.location.pathname.endsWith("/new")
  const isEditMode =
    window.location.pathname.endsWith("/edit") &&
    params["orgId"] &&
    params["feedComponentId"]
  const componentMode: ComponentModes = isCreateMode
    ? "create"
    : isEditMode
      ? "edit"
      : "invalid"

  const org = UserDataContextHelper.findOrg(params["orgId"]!, userData!)
  const feedComponent: FeedComponentDto = isEditMode
    ? UserDataContextHelper.findFeedComponent(org, params["feedComponentId"]!)
    : getEmptyFeedComponent(org)
  // A null `group` (all existing rows, predating this field's UI) would
  // leave the <select> with nothing selected. Default it so an option is
  // always shown/selected.
  const feedComponentGroup = feedComponent.group || FeedComponentDefaultGroup

  const methods = useForm<FeedComponentCreateDto>({
    values: {
      ...feedComponent,
      group: feedComponentGroup,
      // Water has 0% dry matter by definition, regardless of what's stored.
      ts_gehalt_percent: isWaterGroup(feedComponentGroup)
        ? 0
        : feedComponent.ts_gehalt_percent,
      // Stored per kg, but entered per UNIT_OF_LARGE_MASS. Converted back in cleanData.
      price_per_kg:
        typeof feedComponent.price_per_kg === "number"
          ? pricePerKgToPerLargeMass(feedComponent.price_per_kg)
          : feedComponent.price_per_kg,
    },
    mode: "onBlur",
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
  } = methods

  const modalCallback = async (value: string) => {
    console.log("> modalCallback:", value)
    if (value.trim().toLowerCase() === "ja") {
      console.log("wanna delete: " + feedComponent.id)
      const statusCode = await deleteFeedComponent(feedComponent.id)
      if (200 === statusCode) {
        /** @todo set global success message with data from `feedComponent` */
      } else {
        setError("root.serverError", {
          message: `${I18n.get("Fehler")}: ${I18n.get("Konnte die Komponente nicht archivieren. Möglicherweise wird sie noch in einem Rezept verwendet.")}`,
        })
      }

      console.log("Invalidating Cache")
      const cacheKey = "frontendApi#/users/current"
      await Cache.removeItem(cacheKey)

      if (reloadUserDataAsync) {
        console.log("Reloading data")
        await reloadUserDataAsync()
      } else {
        console.warn("Not reloading data; callback not set")
      }

      console.log("Navigating back")
      navigate(getLinkToOrgFeedComponents(org))
    } else {
      console.log("NO wanna delete")
    }
    setShowArchiveModal(false)
  }

  const [selectedComponentImageId, setSelectedComponentImageId] =
    useState<string>(feedComponent.image || "")
  const [bgImageSrc, setBgImageSrc] = useState<string>("")

  // Water has no TS-Gehalt; disable the field while keeping its current
  // value in the form in case the group is de-selected again before saving.
  const isSelectedGroupWater = isWaterGroup(watch("group"))

  if (componentMode === "invalid") {
    return <div>Errrr... invalid component mode</div>
  }

  const onComponentImageClick = (selectedId: string) => {
    console.log(`> onComponentImageClick(${selectedId})`)
    // Update image selector component
    setSelectedComponentImageId(selectedId)
    // Update form data
    setValue("image", selectedId)
    // Update background image
    setBgImageSrc(getImagePath(selectedId))
  }
  ensureImagePath(feedComponent, bgImageSrc, setBgImageSrc)

  const onSubmit: SubmitHandler<FeedComponentCreateDto> = async (data) => {
    console.log("▶️ onSubmit")
    if (isReadOnly) {
      console.log("🚧 Read-only mode.")
      setError("root.clientError", {
        type: "ui.read-only",
        message: "Bearbeiten ist nicht erlaubt.",
      })
      return
    }
    data.org_id = org.id
    setIsFormSubmitting(true)
    const cleaned = cleanData(data, isCreateMode)
    // const clean = data
    // console.log('clean', clean)
    // console.log("\n"
    //   + `${clean.name}\n`
    //   + `  Komponenten\n`
    //   + clean.RecipeComponents.map(rco => {
    //     const fcId = rco.feed_component_id
    //     const fc = AppContextHelper.findFeedComponent(org, fcId)
    //     return `    ${fc.name}\t${rco.mass_kg} kg\t${rco.mixing_time_s} s`
    //   }).join("\n")
    //   + "\n"
    //   + `  Ausladegruppen\n`
    //   + clean.RecipesAusladegruppen.map(rag => {
    //     const agId = rag.ausladegruppe_id
    //     const ag = AppContextHelper.findAusladegruppe(org, agId)
    //     return `    ${ag.name}\t${ag.number_of_animals} T`
    //   }).join("\n")
    // )
    /** @todo Implement proper client-side validation (different for 'create' and 'update') */
    if (!FeedComponentCreateSchema.isValidSync(cleaned)) {
      console.warn("❌ Cleaned data is not valid")
      try {
        await FeedComponentCreateSchema.validate(cleaned, {
          abortEarly: false,
        })
      } catch (err) {
        /** @ts-ignore */
        console.log("Validation errors (cleaned)", err.errors)
        const response: ResponseMessage = {
          res: {
            code: "400",
            /** @ts-ignore */
            message: `${err.errors.length} Errors. The first one is: ${err.errors[0]}`,
          },
          sessionId: "",
        }
        setIsFormSubmitting(false)
        return response
      }
    } else {
      console.log("✅ Cleaned data is valid")
    }
    const result = isEditMode
      ? await updateFeedComponent(
          feedComponent.id,
          cleaned as FeedComponentUpdateDto
        )
      : await createFeedComponent(cleaned as FeedComponentCreateDto)
    /** @todo Result is meaningless? */
    /** @todo Error handling */

    console.log("Invalidating Cache")
    const cacheKey = "frontendApi#/users/current"
    await Cache.removeItem(cacheKey)

    if (reloadUserDataAsync) {
      console.log("Reloading data")
      await reloadUserDataAsync()
    } else {
      console.warn("Not reloading data; callback not set")
    }

    setIsFormSubmitting(false)
    console.log("Navigating back")
    navigate(getLinkToOrgFeedComponents(org))

    return result
  }

  const prepareApiCall = async (path: string, body: any = false) => {
    console.debug("▶️ prepareApiCall")
    const tokens = await currentSession()
    const apiName = "frontendApi"
    const options = {
      headers: {
        Authorization: `Bearer ${tokens?.idToken}`,
      },
      body: body,
    }
    if (!body) {
      delete options.body
    }
    !isProd && console.debug(`Prepared API call to ${path}`)
    return { apiName, path, options }
  }
  async function createFeedComponent(data: FeedComponentCreateDto) {
    console.log("▶️ createFeedComponent")
    const apiCallParams = await prepareApiCall(`/feed-components`, data)
    const restOperation = post(apiCallParams)
    const apiResponse = (await (
      await restOperation.response
    ).body.json()) as unknown as ResponseMessage
    console.log("Got API response:", apiResponse)
    console.log("Got restOperation.response:", restOperation.response)
    // look at `(await restOperation.response).statusCode`, too
    return apiResponse
  }
  async function updateFeedComponent(
    feedComponentId: string,
    data: FeedComponentUpdateDto
  ): Promise<ResponseMessage> {
    // -> PUT
    console.log("▶️ updateFeedComponent")
    // If a user clears an optional attribute it may be null-ish.
    // Make sure it's a real `null`, otherwise it will be stripped by schema validation.
    if (
      !Object.hasOwn(data, "image") ||
      data.image === undefined ||
      data.image === ""
    ) {
      data.image = null
    }
    if (
      !Object.hasOwn(data, "price_per_kg") ||
      data.price_per_kg === undefined ||
      /** @ts-ignore */
      data.price_per_kg === ""
    ) {
      data.price_per_kg = null
    }
    !isProd && console.debug("updateFeedComponent: data =", data)
    const apiCallParams = await prepareApiCall(
      `/feed-components/${feedComponentId}`,
      data
    )
    const restOperation = put(apiCallParams)
    const apiResponse = (await (
      await restOperation.response
    ).body.json()) as unknown as ResponseMessage
    console.log("Got API response:", apiResponse)
    console.log("Got restOperation.response:", restOperation.response)
    // look at `(await restOperation.response).statusCode`, too
    return apiResponse
  }
  async function deleteFeedComponent(feedComponentId: string) {
    console.log("▶️ deleteFeedComponent", feedComponentId)
    const apiCallParams = await prepareApiCall(
      `/feed-components/${feedComponentId}`
    )
    try {
      const response = await delToApi(apiCallParams.apiName, apiCallParams.path) //, {
      // console.log("🪅🪅🪅 Got delToApi.response:", response)
      const statusCode: number = (await response.response).statusCode
      // console.log("Got API response with statusCode:", statusCode)
      // console.log("Got restOperation.response:", restOperation.response)
      // look at `(await restOperation.response).statusCode`, too
      return statusCode
    } catch (err: any) {
      const statusCode = err.statusCode || 500
      // console.log("🩹🩹🩹 Error in API call:", err)
      // console.log("🩹🩹🩹 Error in API call:", getErrorMessage(err))
      return statusCode
    }
  }

  return (
    <>
      <div className="rm-edit-page">
        <div
          className="hdr-img"
          style={{ backgroundImage: `url('${bgImageSrc}')` }}
        />
        <div className="content-overlay">
          <div className="header">
            <div className="info">
              <h2>{feedComponent.name}</h2>
            </div>
          </div>

          <div className="details">
            {/* pass all methods into the context */}
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Flex direction="column" gap="small">
                  <Flex direction="row" gap="small" className="field-row">
                    <Label htmlFor="name">Komponentenname:</Label>
                    <input
                      {...register("name", {
                        required: "Geben Sie den Namen der Komponente an.",
                        maxLength: {
                          value: FeedComponentSchemaNameMaxLength,
                          message: `Der Name darf max. ${FeedComponentSchemaNameMaxLength} Zeichen lang sein.`,
                        },
                      })}
                      placeholder={feedComponent.name || "Neue Komponente"}
                      className={errors.name ? "error" : ""}
                    />
                    {errors.name && <div>Fehler: {errors.name.message}</div>}
                  </Flex>

                  <Flex direction="row" gap="small" className="field-row">
                    <Label htmlFor="ts_gehalt_percent">TS-Gehalt:</Label>
                    <Flex alignItems="center" gap="small">
                      <Controller
                        control={control}
                        name="ts_gehalt_percent"
                        render={({ field }) => (
                          <input
                            {...field}
                            /** @ts-ignore */
                            className={errors.ts_gehalt_percent ? "error" : ""}
                            disabled={isSelectedGroupWater}
                            onBlur={(e) => {
                              field.onChange(
                                e.target.value === ""
                                  ? 0
                                  : transformInputToNumber(e.target.value)
                              )
                              field.onBlur()
                            }}
                            value={transformNumberToString(field.value ?? 0)}
                          />
                        )}
                        rules={{
                          required: false,
                          min: {
                            value: 0,
                            message: "Geben Sie einen positiven TS-Gehalt ein.",
                          },
                          max: {
                            value: 100,
                            message: "Der TS-Gehalt kann max. 100 % sein.",
                          },
                        }}
                      />
                      %
                    </Flex>
                  </Flex>
                  {errors.ts_gehalt_percent && (
                    <div className="error">
                      Fehler: {errors.ts_gehalt_percent.message}
                    </div>
                  )}

                  <Flex direction="row" gap="small" className="field-row">
                    <Label htmlFor="price_per_kg">Preis:</Label>
                    <Flex alignItems="center" gap="small">
                      <Controller
                        control={control}
                        name="price_per_kg"
                        render={({ field }) => (
                          <input
                            {...field}
                            /** @ts-ignore */
                            className={errors.price_per_kg ? "error" : ""}
                            onBlur={(e) => {
                              field.onChange(
                                e.target.value === ""
                                  ? undefined
                                  : transformInputToNumber(e.target.value)
                              )
                              field.onBlur()
                            }}
                            value={transformNumberToString(field.value ?? "")}
                          />
                        )}
                        rules={{
                          required: false,
                          min: {
                            value: 0,
                            message: "Geben Sie einen positiven Preis ein.",
                          },
                        }}
                      />
                      {CURRENCY_SYMBOL} / {UNIT_OF_LARGE_MASS} Frischmasse
                    </Flex>
                  </Flex>
                  {errors.price_per_kg && (
                    <div className="error">
                      Fehler: {errors.price_per_kg.message}
                    </div>
                  )}

                  <Flex direction="row" gap="small">
                    <SwitchField
                      id="gegenschneiden_ausfahren"
                      name="gegenschneiden_ausfahren"
                      label="Gegenschneiden in den Behälter einfahren:"
                      defaultChecked={
                        feedComponent.gegenschneiden_ausfahren ?? false
                      }
                      checked={watch("gegenschneiden_ausfahren")}
                      onChange={({ target }) => {
                        setValue("gegenschneiden_ausfahren", target.checked)
                      }}
                    />
                  </Flex>

                  <Flex
                    direction="row"
                    gap="small"
                    alignItems="center"
                    className="field-row"
                  >
                    <Label htmlFor="elevatorband_geschwindigkeit">
                      Elevatorband Geschwindigkeit *:
                    </Label>
                    <Flex alignItems="center" gap="small">
                      <input
                        {...register("elevatorband_geschwindigkeit", {
                          required: false,
                          min: {
                            value: 0,
                            message:
                              "Die Elevatorband-Geschwindigkeit muss mindestens 0 % sein.",
                          },
                          max: {
                            value: 100,
                            message:
                              "Die Elevatorband-Geschwindigkeit darf maximal 100 % sein.",
                          },
                        })}
                        className={
                          errors.elevatorband_geschwindigkeit ? "error" : ""
                        }
                      />
                      %
                    </Flex>
                  </Flex>
                  {errors.elevatorband_geschwindigkeit && (
                    <div className="error">
                      Fehler: {errors.elevatorband_geschwindigkeit.message}
                    </div>
                  )}

                  <Flex direction="row" gap="small" className="field-row">
                    <Label htmlFor="group">Futtermittel-Gruppe:</Label>
                    <select
                      {...register("group")}
                      className={errors.group ? "error" : ""}
                    >
                      {FEED_COMPONENT_GROUP_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {I18n.get(option.label)}
                        </option>
                      ))}
                    </select>
                  </Flex>
                  {errors.group && (
                    <div className="error">Fehler: {errors.group.message}</div>
                  )}
                  <Flex direction="row" gap="small">
                    <SwitchField
                      id="include_in_stats"
                      name="include_in_stats"
                      label="In Statistiken berücksichtigen:"
                      defaultChecked={feedComponent.include_in_stats ?? true}
                      checked={watch("include_in_stats")}
                      onChange={({ target }) => {
                        setValue("include_in_stats", target.checked)
                      }}
                    />
                  </Flex>

                  <div>
                    <Label className="!block !mb-2">Bild auswählen:</Label>
                    <input {...register("image")} type="hidden" />
                    <Collection
                      className="w-full md:!w-2/3"
                      type="list"
                      direction="row"
                      wrap="wrap"
                      items={componentImages}
                    >
                      {(item) => {
                        const imageId = item.title + "#" + item.href
                        const isActive = imageId === selectedComponentImageId
                        return (
                          <Card
                            key={imageId}
                            className="rm-list-item small !border-none !items-start !pb-1"
                          >
                            <Flex
                              direction="column"
                              gap="2px"
                              onClick={() => onComponentImageClick(imageId)}
                              className={
                                "!cursor-pointer !rounded !border-2 !border-solid" +
                                (isActive
                                  ? " !border-bsl-green"
                                  : " !border-transparent")
                              }
                            >
                              <div
                                className={
                                  "img" +
                                  (isActive
                                    ? " !rounded-t !rounded-b-none"
                                    : "")
                                }
                                style={{
                                  backgroundImage: `url('/images/fc/${item.href}')`,
                                }}
                              />
                              <div
                                className={
                                  "p-1 w-14 text-center break-words [hyphens:auto]" +
                                  (isActive
                                    ? " bg-bsl-green text-white font-semibold"
                                    : "")
                                }
                              >
                                {item.title}
                              </div>
                            </Flex>
                          </Card>
                        )
                      }}
                    </Collection>
                  </div>
                  <Flex>
                    * Eine Bandgeschwindigkeit von 0 % bedeutet, dass die
                    Geschwindigkeit über den Regler in der Armlehne geregelt
                    wird.
                  </Flex>

                  {errors.root && errors.root["clientError"] && (
                    <p className="error">
                      {errors.root["clientError"].message}
                    </p>
                  )}
                  {errors.root && errors.root["serverError"] && (
                    <p className="error">
                      {errors.root["serverError"].message}
                    </p>
                  )}

                  <div className="buttons">
                    <Button
                      type="submit"
                      variation="primary"
                      isDisabled={isFormSubmitting || isReadOnly}
                      isLoading={isFormSubmitting}
                      loadingText={I18n.get("Wird gespeichert...")}
                    >
                      Speichern
                    </Button>
                    <CancelButton
                      href=".."
                      title="Abbrechen"
                      isDisabled={isFormSubmitting}
                    />
                    {componentMode === "edit" && (
                      <Button
                        isDisabled={isFormSubmitting || isReadOnly}
                        variation="warning"
                        onClick={() => {
                          setShowArchiveModal(true)
                        }}
                      >
                        Archivieren
                      </Button>
                    )}
                    {componentMode === "edit" && showArchiveModal && (
                      <Modal
                        buttons={[
                          {
                            text: "Ja",
                            variation: "destructive",
                          },
                          { text: "Nein" },
                        ]}
                        show={showArchiveModal}
                        callback={modalCallback}
                      >
                        <div className="title">
                          Komponente wirklich archivieren?
                        </div>
                        <div>
                          Wollen Sie die Komponente "{feedComponent.name}"
                          wirklich archivieren?
                        </div>
                        <div>
                          Wenn ein Eintrag archiviert ist, kann er nicht mehr
                          bearbeitet werden, und wird nicht mehr zu den
                          Maschinen übertragen.
                          <br />
                          In Statistiken taucht er weiterhin auf.
                        </div>
                      </Modal>
                    )}
                  </div>
                </Flex>
              </form>
            </FormProvider>

            {/* {componentMode === "edit"
            &&
            <div className="mt-5 text-xs">
              <div>Zuletzt bearbeitet am {feedComponent.updated_at} von {feedComponent.updated_by}</div>
              <div>Angelegt am {feedComponent.created_at} von {feedComponent.created_by}</div>
            </div>
          } */}
          </div>
        </div>
      </div>

      {/* <small>* Die Icons "xmark (light)", "pen-to-square (light)" benötigen FontAwesome Pro, kostet 49,- oder 99,- pro Jahr.</small> */}
    </>
  )
}
